import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {Container, Box, CssBaseline, Typography, Alert, Button} from '@mui/material';
import theme from './theme';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Products from './components/Products';
import Bundles from './components/Bundles';
import Notifications from './components/Notifications';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Billing1 from './components/Billing';
import Profile from './components/Profile';
import Layout from './Layout';
import axios from "axios";
import Report from './components/reports/Report';
import PackBundle from './components/PackBundle';
import EcwidProduct from './components/EcwidProduct';
import StoreOptions from './components/StoreOptions';
import ProductOptionsManager from './components/StoreOptions';
import Onboarding from './components/Onboarding';

const stripePromise = loadStripe('pk_live_51Rh71YLB25qpjPjOdBTbVUGHKmAcD0s4tEdAEhGjVeilfKKX8qyyvgV0h4iwYGFPmqYozpgJ0lHukDWAAKteVQNn00vOtVKWTj');
const App = () => {
    const [products, setProducts] = useState([]);
    const [storeActive, setStoreActive] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [noteCount, setNoteCount] = useState([]);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accessToken, setAccessToken] = useState(null);
    const [storeId, setStoreId] = useState(null);
    const [publicToken, setPublicToken] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [hasCombinations, setHasCombinations] = useState(true);
    const [announcement, setAnnouncement] = useState(null);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);

    const getNotifications = async () => {
        try {
            const response = await api.get('/api/notifications?page=1&limit=10');
            setNotifications(response.data.notifications);
            setNoteCount(response.data.unreadCount);
        } catch (error) {
            console.error(error);
        }
    };

    const getActiveAnnouncement = async () => {
        try {
            const response = await api.get('/api/announcements/active');
            const active = response.data.announcement;
            const dismissedId = localStorage.getItem('dismissed_announcement_id');
            if (active && String(dismissedId) === String(active.id)) {
                setAnnouncement(null);
            } else {
                setAnnouncement(active || null);
            }
        } catch (error) {
            console.error('Error fetching announcement:', error);
        }
    };

    const dismissAnnouncement = () => {
        if (announcement) {
            localStorage.setItem('dismissed_announcement_id', announcement.id);
        }
        setAnnouncement(null);
    };

    const ECWID_CLIENT_ID = process.env.REACT_APP_ECWID_CLIENT_ID;
    const ECWID_CLIENT_SECRET = process.env.REACT_APP_ECWID_CLIENT_SECRET;
    const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;

    const api = React.useMemo(() => {
        const instance = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
        });

        instance.interceptors.request.use((config) => {
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`;
                config.headers.store_id = `${storeId}`;
                config.headers.public_token = `${publicToken}`;
            }
            return config;
        });

        return instance;
    }, [accessToken, storeId, publicToken]);

    // Handle OAuth authentication
    const handleAuth = () => {
        const scope = 'read_products write_products read_catalog write_catalog, update_catalog, create_catalog, read_orders';
        const authUrl = `https://my.ecwid.com/api/oauth/authorize?client_id=${ECWID_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${encodeURIComponent(scope)}&response_type=code`;
        // console.log(authUrl);return;
        window.location.href = authUrl;
    };

    // Handle OAuth callback
    useEffect(() => {
        const handleOAuthCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');

            if (code) {
                try {
                    const response = await api.post('/api/oauth/token', {
                        code,
                        client_id: ECWID_CLIENT_ID,
                        client_secret: ECWID_CLIENT_SECRET,
                        redirect_uri: REDIRECT_URI,
                        grant_type: 'authorization_code'
                    });

                    const { access_token, store_id, public_token } = response.data;
                    setAccessToken(access_token);
                    setStoreId(store_id);
                    setIsAuthenticated(true);
                    setPublicToken(public_token);
                    // Remove code from URL
                    window.history.replaceState({}, document.title, window.location.pathname);

                    // Store tokens in localStorage
                    localStorage.setItem('ecwid_access_token', access_token);
                    localStorage.setItem('ecwid_store_id', store_id);
                    localStorage.setItem('ecwid_public_token', public_token);
                    window.location.href = "/";
                } catch (error) {
                    console.error('OAuth error:', error);
                    setAuthError('Failed to authenticate with Ecwid');
                }
            }
        };

        // Check for stored tokens
        const storedToken = localStorage.getItem('ecwid_access_token');
        const storedStoreId = localStorage.getItem('ecwid_store_id');
        const storedPublicToken = localStorage.getItem('ecwid_public_token');

        if (storedToken && storedStoreId) {
            setAccessToken(storedToken);
            setStoreId(storedStoreId);
            setPublicToken(storedPublicToken);
            setIsAuthenticated(true);
        } else if (window.location.search.includes('code=')) {
            handleOAuthCallback();
        }
    }, []);

    // Logout function
    const handleLogout = () => {
        setAccessToken(null);
        setStoreId(null);
        setPublicToken(null);
        setIsAuthenticated(false);
        localStorage.removeItem('ecwid_access_token');
        localStorage.removeItem('ecwid_store_id');
        localStorage.removeItem('ecwid_public_token');
    };

    /*const getProducts = () => {
        api.get('/api/products')
            .then(response => {
                setProducts(response.data.items);
            })
            .catch(error => console.error(error));
    };*/

    const checkStoreStatus = async () => {
        try {
            const response = await api.get('/api/store');
            const storeDetails = response.data.details;

            // Case 1: New store — onboarding not yet completed (billing_name is blank, active is false)
            const needsOnboard = !storeDetails?.active && (!storeDetails?.billing_name || storeDetails.billing_name.trim() === '');
            if (needsOnboard) {
                setNeedsOnboarding(true);
                return;
            }

            // Case 2: Trial expired or manually deactivated — lock to billing page
            const trialExpired = storeDetails?.trial && storeDetails?.billing_date && new Date(storeDetails.billing_date) < new Date();
            if (trialExpired || storeDetails?.active === false) {
                setStoreActive(false);
                if (!window.location.pathname.startsWith('/billing') && !window.location.pathname.startsWith('/profile')) {
                    window.location.href = '/billing';
                }
                return;
            }

            // Case 3: Active store
            setStoreActive(true);
            setNeedsOnboarding(false);

            const features = response.data.store?.account?.availableFeatures || [];
            if (!features.includes('COMBINATIONS')) {
                setHasCombinations(false);
            } else {
                setHasCombinations(true);
            }
        } catch (error) {
            console.error('Error checking store status:', error);
        }
    };
    useEffect(() => {
        if (isAuthenticated) {
           /*getProducts();*/
            getNotifications();
            checkStoreStatus();
            getActiveAnnouncement();
            const interval = setInterval(getActiveAnnouncement, 5 * 60 * 1000);
            return () => clearInterval(interval);
        }
    }, [isAuthenticated, api]);

    if (!isAuthenticated) {
        return (
            <Container>
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="h4" gutterBottom>
                        Welcome to Ecwid Plugin Admin
                    </Typography>
                    {authError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {authError}
                        </Alert>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAuth}
                        size="large"
                    >
                        Connect with Ecwid
                    </Button>
                </Box>
            </Container>
        );
    }
    if (needsOnboarding) {
        return (
            <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <Routes>
                    <Route path="*" element={
                        <Elements stripe={stripePromise}>
                            <Onboarding
                                api={api}
                                onComplete={() => { setNeedsOnboarding(false); setStoreActive(true); }}
                            />
                        </Elements>
                    }/>
                </Routes>
            </Router>
            </ThemeProvider>
        );
    }
    if (!storeActive) {
        return (
            <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <Layout handleLogout={handleLogout} notifications={notifications} noteCount={noteCount} announcement={announcement} onDismissAnnouncement={dismissAnnouncement}>
                    <Routes>
                        <Route path="/profile" element={<Profile api={api} isAuthenticated={isAuthenticated}/>}/>
                        <Route path="/billing" element={<Elements stripe={stripePromise}>
                            <Billing1 api={api} isAuthenticated={isAuthenticated}/>
                        </Elements>}/>
                    </Routes>
                </Layout>
            </Router>
            </ThemeProvider>
        );
    }else {

        return (
            <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <Layout handleLogout={handleLogout} notifications={notifications} noteCount={noteCount} announcement={announcement} onDismissAnnouncement={dismissAnnouncement}>
                    <Routes>
                        <Route path="/reports" element={<Report api={api} isAuthenticated={isAuthenticated}/>}/>
                        <Route path="/products" element={<Products api={api} isAuthenticated={isAuthenticated}/>}/>
                        <Route path="/bundles" element={<Bundles api={api} isAuthenticated={isAuthenticated}/>}/>
                        <Route path="/packbundles"
                               element={<EcwidProduct api={api} isAuthenticated={isAuthenticated} hasCombinations={hasCombinations}/>}/>
                        <Route path="/storeoptions"
                               element={<ProductOptionsManager api={api} isAuthenticated={isAuthenticated}/>}/>
                        <Route path="/notifications"
                               element={<Notifications api={api} isAuthenticated={isAuthenticated}/>}/>
                        <Route path="/profile" element={<Profile api={api} isAuthenticated={isAuthenticated}/>}/>
                        <Route path="/billing" element={<Elements stripe={stripePromise}>
                            <Billing1 api={api} isAuthenticated={isAuthenticated}/>
                        </Elements>}/>
                        <Route path="/" element={<Dashboard api={api} isAuthenticated={isAuthenticated}/>}/>
                    </Routes>
                </Layout>
            </Router>
            </ThemeProvider>
        );
    }
};

export default App;